/**
 * Nice Cube storefront enhancements.
 * Kept dependency-free and layered on top of the original theme so the core
 * product template / variant engine remains untouched.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    if (window.Currency && typeof window.Currency.formatMoney === 'function') {
      return window.Currency.formatMoney(cents, window.theme && window.theme.moneyFormat ? window.theme.moneyFormat : '${{amount}}');
    }
    return '$' + (Number(cents || 0) / 100).toFixed(2).replace(/\.00$/, '');
  }

  function stripAddToCartPrice(root) {
    var context = root || document;
    var buttons = context.querySelectorAll ? context.querySelectorAll('.ProductForm__AddToCart') : [];

    for (var i = 0; i < buttons.length; i += 1) {
      var price = buttons[i].querySelector('[data-money-convertible]');
      var dot = buttons[i].querySelector('.Button__SeparatorDot');
      if (price) price.remove();
      if (dot) dot.remove();
    }
  }

  function updateBundlePrices(selector, unitPrice) {
    if (!selector) return;
    selector.setAttribute('data-unit-price', unitPrice);

    var options = selector.querySelectorAll('[data-bundle-option]');
    for (var i = 0; i < options.length; i += 1) {
      var option = options[i];
      var quantity = parseInt(option.getAttribute('data-quantity'), 10) || 1;
      var paidQuantity = parseInt(option.getAttribute('data-paid-quantity'), 10) || quantity;
      var discount = parseInt(option.getAttribute('data-discount'), 10) || 0;
      var freeQuantity = parseInt(option.getAttribute('data-free-quantity'), 10) || 0;
      var compareTotal = unitPrice * quantity;
      var paidBase = unitPrice * paidQuantity;
      var total = freeQuantity > 0 ? paidBase : Math.round(paidBase * (100 - discount) / 100);
      var price = option.querySelector('[data-bundle-price]');
      var compare = option.querySelector('[data-bundle-compare]');
      var save = option.querySelector('[data-bundle-save]');
      var saveLabel = option.getAttribute('data-save-label') || (discount > 0 ? 'Save ' + discount + '%' : '');

      if (price) price.textContent = formatMoney(total);
      if (compare) {
        compare.textContent = formatMoney(compareTotal);
        compare.hidden = !(discount > 0 || freeQuantity > 0);
      }
      if (save) {
        save.textContent = saveLabel;
        save.hidden = !saveLabel;
      }
    }
  }

  function getBundleProductData(selector) {
    var script = selector ? selector.querySelector('[data-bundle-product-json]') : null;
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch (e) { return null; }
  }

  function getBundleColors(selector) {
    var script = selector ? selector.querySelector('[data-bundle-colors-json]') : null;
    if (!script) return [];
    try { return JSON.parse(script.textContent) || []; } catch (e) { return []; }
  }

  function getCurrentVariant(form, productData) {
    if (!form || !productData || !productData.variants) return null;
    var idInput = form.querySelector('[name="id"]');
    var id = idInput ? String(idInput.value) : '';
    for (var i = 0; i < productData.variants.length; i += 1) {
      if (String(productData.variants[i].id) === id) return productData.variants[i];
    }
    return null;
  }

  function findVariantForColor(productData, currentVariant, colorPosition, colorValue) {
    if (!productData || !currentVariant || !colorPosition) return null;
    var target = [currentVariant.option1, currentVariant.option2, currentVariant.option3];
    target[colorPosition - 1] = colorValue;

    for (var i = 0; i < productData.variants.length; i += 1) {
      var variant = productData.variants[i];
      if (variant.option1 === target[0] && variant.option2 === target[1] && variant.option3 === target[2]) return variant;
    }
    return null;
  }

  function showBundleColorPanel(selector, option) {
    var panels = selector.querySelectorAll('[data-bundle-color-panel]');
    var key = option ? option.getAttribute('data-color-count') : '';
    for (var i = 0; i < panels.length; i += 1) {
      panels[i].hidden = panels[i].getAttribute('data-bundle-color-panel') !== key;
    }
  }

  function addMixedBundle(form, selector, selectedOption, done) {
    var productData = getBundleProductData(selector);
    var colorPosition = parseInt(selector.getAttribute('data-color-position'), 10) || 0;
    var currentVariant = getCurrentVariant(form, productData);
    if (!productData || !currentVariant || !colorPosition) {
      done(false);
      return;
    }

    var colors = [];
    if (selectedOption.getAttribute('data-assorted') === 'true') {
      var allColors = getBundleColors(selector);
      for (var a = 0; a < allColors.length && a < 4; a += 1) colors.push(allColors[a]);
      if (colors.length < 4) {
        done(false);
        return;
      }
      colors.push(currentVariant['option' + colorPosition]);
    } else {
      var panelKey = selectedOption.getAttribute('data-color-count');
      var panel = selector.querySelector('[data-bundle-color-panel="' + panelKey + '"]');
      var selects = panel ? panel.querySelectorAll('[data-bundle-color]') : [];
      for (var s = 0; s < selects.length; s += 1) colors.push(selects[s].value);
    }

    var lineMap = {};
    for (var i = 0; i < colors.length; i += 1) {
      var variant = findVariantForColor(productData, currentVariant, colorPosition, colors[i]);
      if (!variant || !variant.available) {
        done(false);
        return;
      }
      var key = String(variant.id);
      if (!lineMap[key]) lineMap[key] = { id: variant.id, quantity: 0 };
      lineMap[key].quantity += 1;
    }

    var items = [];
    for (var id in lineMap) {
      if (Object.prototype.hasOwnProperty.call(lineMap, id)) items.push(lineMap[id]);
    }
    if (!items.length) {
      done(false);
      return;
    }

    var button = form.querySelector('.ProductForm__AddToCart');
    if (button) button.disabled = true;
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    fetch('/cart/add.js', {
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (!response.ok) throw new Error('Unable to add bundle');
      return response.json();
    }).then(function () {
      if (button) button.disabled = false;
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      form.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: {
          variant: currentVariant,
          quantity: colors.length
        }
      }));
      done(true);
    }).catch(function () {
      if (button) button.disabled = false;
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      done(false);
    });
  }

  function initBundleSelector(productSection) {
    var selector = productSection.querySelector('[data-bundle-selector]');
    if (!selector || selector.getAttribute('data-bundle-ready') === 'true') return;
    selector.setAttribute('data-bundle-ready', 'true');

    var form = selector.closest('form.ProductForm');
    if (!form) return;

    var quantityInput = form.querySelector('input[name="quantity"]');
    var options = selector.querySelectorAll('[data-bundle-option]');
    var initialUnitPrice = parseInt(selector.getAttribute('data-unit-price'), 10) || 0;
    updateBundlePrices(selector, initialUnitPrice);

    function getSelectedOption() {
      return selector.querySelector('[data-bundle-option].is-selected');
    }

    function markSelected(button) {
      for (var i = 0; i < options.length; i += 1) {
        var isSelected = options[i] === button;
        options[i].classList.toggle('is-selected', isSelected);
        options[i].setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      }
      showBundleColorPanel(selector, button);
    }

    for (var i = 0; i < options.length; i += 1) {
      options[i].addEventListener('click', function () {
        var quantity = parseInt(this.getAttribute('data-quantity'), 10) || 1;
        markSelected(this);
        if (quantityInput) {
          quantityInput.value = quantity;
          quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    var initiallySelected = getSelectedOption() || options[0];
    if (initiallySelected) {
      var initialQuantity = parseInt(initiallySelected.getAttribute('data-quantity'), 10) || 1;
      markSelected(initiallySelected);
      if (quantityInput) quantityInput.value = initialQuantity;
    }

    form.addEventListener('submit', function (event) {
      var selectedOption = getSelectedOption();
      if (!selectedOption || selectedOption.getAttribute('data-multicolor') !== 'true') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      addMixedBundle(form, selector, selectedOption, function (success) {
        if (!success) {
          var quantity = parseInt(selectedOption.getAttribute('data-quantity'), 10) || 1;
          if (quantityInput) quantityInput.value = quantity;
          var button = form.querySelector('.ProductForm__AddToCart');
          if (button) {
            button.disabled = false;
            var warning = document.createElement('span');
            warning.className = 'ProductForm__Error Alert Alert--error';
            warning.textContent = 'One of the selected colors is unavailable. Please choose another color.';
            button.insertAdjacentElement('afterend', warning);
            window.setTimeout(function () { if (warning.parentNode) warning.parentNode.removeChild(warning); }, 3200);
          }
        }
      });
    }, true);
  }

  function initProductSection(productSection) {
    if (!productSection || productSection.getAttribute('data-store-enhanced') === 'true') return;
    productSection.setAttribute('data-store-enhanced', 'true');

    var saleBadge = productSection.querySelector('[data-sale-badge]');
    var salePercentage = productSection.querySelector('[data-sale-percentage]');
    var stockStatus = productSection.querySelector('[data-stock-status]');
    var stockLabel = productSection.querySelector('[data-stock-label]');
    var stickyBar = productSection.querySelector('[data-product-sticky-bar]');
    var stickyButton = stickyBar ? stickyBar.querySelector('[data-sticky-add]') : null;
    var stickyLabel = stickyBar ? stickyBar.querySelector('[data-sticky-label]') : null;
    var buyNowButton = productSection.querySelector('[data-direct-checkout]');
    var bundleSelector = productSection.querySelector('[data-bundle-selector]');

    initBundleSelector(productSection);
    stripAddToCartPrice(productSection);

    function getMainButton() {
      return productSection.querySelector('.ProductForm__AddToCart');
    }

    function syncVariantIndicators(variant) {
      if (!variant) {
        if (saleBadge) saleBadge.hidden = true;
        if (stockStatus) stockStatus.hidden = true;
        return;
      }

      var isOnSale = Boolean(variant.compare_at_price && variant.compare_at_price > variant.price);
      if (saleBadge) saleBadge.hidden = !isOnSale;
      if (salePercentage && isOnSale) {
        salePercentage.textContent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
      }
      if (stockStatus) {
        stockStatus.hidden = false;
        stockStatus.classList.toggle('is-unavailable', !variant.available);
      }
      if (stockLabel) stockLabel.textContent = variant.available ? 'In stock' : 'Sold out';
      if (stickyButton) stickyButton.disabled = !variant.available;
      if (buyNowButton) buyNowButton.disabled = !variant.available;
      if (bundleSelector) updateBundlePrices(bundleSelector, variant.price);
    }

    function syncStickyBar() {
      var mainButton = getMainButton();
      if (!stickyBar || !mainButton || !stickyButton) return;
      var mainLabel = mainButton.querySelector('span:first-child');
      stickyButton.disabled = mainButton.disabled;
      if (stickyLabel && mainLabel) stickyLabel.textContent = mainLabel.textContent.trim();
    }

    function setStickyVisible(isVisible) {
      if (!stickyBar) return;
      stickyBar.classList.toggle('is-visible', isVisible);
      stickyBar.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    }

    function handleVariantChanged(event) {
      syncVariantIndicators(event.detail && event.detail.variant);
      window.requestAnimationFrame(function () {
        stripAddToCartPrice(productSection);
        syncStickyBar();
      });
      window.setTimeout(function () {
        stripAddToCartPrice(productSection);
        syncStickyBar();
      }, 90);
    }

    productSection.addEventListener('variant:changed', handleVariantChanged);

    if (!stickyBar || !stickyButton) return;

    stickyButton.addEventListener('click', function () {
      var mainButton = getMainButton();
      if (mainButton && !mainButton.disabled) mainButton.click();
    });

    document.addEventListener('product:added', function () { setStickyVisible(false); });
    syncStickyBar();

    if ('IntersectionObserver' in window) {
      var mainButton = getMainButton();
      if (mainButton) {
        var observer = new IntersectionObserver(function (entries) {
          var entry = entries[0];
          setStickyVisible(!entry.isIntersecting && window.pageYOffset > 260);
        }, { threshold: 0.12 });
        observer.observe(mainButton);
      }
    }
  }

  function autoplayReelVideos(track) {
    var videos = track.querySelectorAll('video');
    for (var i = 0; i < videos.length; i += 1) {
      videos[i].muted = true;
      videos[i].loop = true;
      videos[i].playsInline = true;
      videos[i].setAttribute('muted', '');
      videos[i].setAttribute('playsinline', '');
      videos[i].setAttribute('autoplay', '');
    }

    if (!('IntersectionObserver' in window)) {
      for (var j = 0; j < videos.length; j += 1) {
        var fallbackPlay = videos[j].play();
        if (fallbackPlay && typeof fallbackPlay.catch === 'function') fallbackPlay.catch(function () {});
      }
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      for (var k = 0; k < entries.length; k += 1) {
        var video = entries[k].target;
        var card = video.closest('[data-reel-card]');
        if (entries[k].isIntersecting && entries[k].intersectionRatio >= 0.35) {
          var promise = video.play();
          if (promise && typeof promise.catch === 'function') promise.catch(function () {});
          if (card) card.classList.add('is-playing');
        } else {
          video.pause();
          if (card) card.classList.remove('is-playing');
        }
      }
    }, { root: track, threshold: [0, 0.35, 0.7] });

    for (var n = 0; n < videos.length; n += 1) observer.observe(videos[n]);
  }

  function initReelSlider(slider) {
    if (slider.getAttribute('data-reels-ready') === 'true') return;
    slider.setAttribute('data-reels-ready', 'true');

    var section = slider.closest('.NeedohReels');
    var track = slider.querySelector('[data-reels-track]');
    var prev = section ? section.querySelector('[data-reels-prev]') : null;
    var next = section ? section.querySelector('[data-reels-next]') : null;
    if (!track) return;

    var originals = Array.prototype.slice.call(track.querySelectorAll('[data-reel-card]'));
    if (!originals.length) return;

    for (var i = 0; i < originals.length; i += 1) originals[i].setAttribute('data-reel-original', 'true');

    if (originals.length > 1) {
      var before = [];
      var after = [];
      for (var b = 0; b < originals.length; b += 1) {
        var beforeClone = originals[b].cloneNode(true);
        beforeClone.removeAttribute('data-reel-original');
        beforeClone.setAttribute('data-reel-clone', 'before');
        beforeClone.removeAttribute('id');
        before.push(beforeClone);

        var afterClone = originals[b].cloneNode(true);
        afterClone.removeAttribute('data-reel-original');
        afterClone.setAttribute('data-reel-clone', 'after');
        afterClone.removeAttribute('id');
        after.push(afterClone);
      }
      for (var p = before.length - 1; p >= 0; p -= 1) track.insertBefore(before[p], track.firstChild);
      for (var a = 0; a < after.length; a += 1) track.appendChild(after[a]);
    }

    var jumping = false;
    function metrics() {
      var firstOriginal = track.querySelector('[data-reel-original="true"]');
      var firstAfter = track.querySelector('[data-reel-clone="after"]');
      if (!firstOriginal || !firstAfter) return null;
      return {
        middleStart: firstOriginal.offsetLeft,
        setWidth: firstAfter.offsetLeft - firstOriginal.offsetLeft
      };
    }

    function centerMiddleCard() {
      var currentOriginals = track.querySelectorAll('[data-reel-original="true"]');
      if (!currentOriginals.length) return;
      var middle = currentOriginals[Math.floor(currentOriginals.length / 2)];
      var left = middle.offsetLeft - Math.max(0, (track.clientWidth - middle.offsetWidth) / 2);
      track.scrollLeft = left;
    }

    function normalizeInfiniteScroll() {
      if (jumping) return;
      var m = metrics();
      if (!m || !m.setWidth) return;
      var leftBoundary = m.middleStart - (m.setWidth * 0.55);
      var rightBoundary = m.middleStart + (m.setWidth * 1.55);
      if (track.scrollLeft < leftBoundary) {
        jumping = true;
        track.scrollLeft += m.setWidth;
        window.requestAnimationFrame(function () { jumping = false; });
      } else if (track.scrollLeft > rightBoundary) {
        jumping = true;
        track.scrollLeft -= m.setWidth;
        window.requestAnimationFrame(function () { jumping = false; });
      }
    }

    track.addEventListener('scroll', normalizeInfiniteScroll, { passive: true });

    function scrollByCards(direction) {
      var card = track.querySelector('[data-reel-card]');
      var amount = card ? card.offsetWidth + 16 : Math.max(240, track.clientWidth * 0.7);
      track.scrollBy({ left: amount * direction, behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { scrollByCards(-1); });
    if (next) next.addEventListener('click', function () { scrollByCards(1); });

    window.requestAnimationFrame(function () {
      centerMiddleCard();
      autoplayReelVideos(track);
    });
  }

  function initReels(root) {
    var context = root || document;
    var sliders = context.querySelectorAll ? context.querySelectorAll('[data-reels-slider]') : [];
    for (var i = 0; i < sliders.length; i += 1) initReelSlider(sliders[i]);

    if (document.documentElement.getAttribute('data-reel-play-ready') !== 'true') {
      document.documentElement.setAttribute('data-reel-play-ready', 'true');
      document.addEventListener('click', function (event) {
        var button = event.target.closest ? event.target.closest('[data-reel-play]') : null;
        if (!button) return;
        var card = button.closest('[data-reel-card]');
        var video = card ? card.querySelector('video') : null;
        if (!video) return;
        event.preventDefault();
        if (video.paused) {
          var promise = video.play();
          if (promise && typeof promise.catch === 'function') promise.catch(function () {});
          if (card) card.classList.add('is-playing');
        } else {
          video.pause();
          if (card) card.classList.remove('is-playing');
        }
      });
    }
  }

  function initAllProductSections(root) {
    var context = root || document;
    var sections = context.querySelectorAll ? context.querySelectorAll('.Product--conversionOptimized[data-section-type="product"]') : [];
    for (var i = 0; i < sections.length; i += 1) initProductSection(sections[i]);
    if (context.matches && context.matches('.Product--conversionOptimized[data-section-type="product"]')) initProductSection(context);
  }

  function initDirectCheckout() {
    if (document.documentElement.getAttribute('data-direct-checkout-ready') === 'true') return;
    document.documentElement.setAttribute('data-direct-checkout-ready', 'true');

    document.addEventListener('click', function (event) {
      var button = event.target.closest ? event.target.closest('[data-direct-checkout]') : null;
      if (!button || button.disabled) return;
      var form = button.closest('form.ProductForm');
      if (!form) return;

      var selectedBundle = form.querySelector('[data-bundle-option].is-selected[data-multicolor="true"]');
      if (selectedBundle) return;

      event.preventDefault();
      var label = button.querySelector('[data-direct-checkout-label]');
      var originalLabel = label ? label.textContent : 'Buy now';
      button.disabled = true;
      button.classList.add('is-loading');
      if (label) label.textContent = 'Opening checkout…';

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
      var formData = new FormData(form);

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData,
        credentials: 'same-origin'
      }).then(function (response) {
        if (!response.ok) throw new Error('Unable to add item');
        return response.json();
      }).then(function () {
        window.location.href = root + 'checkout';
      }).catch(function () {
        button.disabled = false;
        button.classList.remove('is-loading');
        if (label) label.textContent = originalLabel;
        window.location.href = root + 'cart';
      });
    });
  }

  function boot(root) {
    initAllProductSections(root || document);
    initDirectCheckout();
    initReels(root || document);
    stripAddToCartPrice(root || document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('variant:changed', function (event) {
    var scope = event.target && event.target.querySelectorAll ? event.target : document;
    window.requestAnimationFrame(function () { stripAddToCartPrice(scope); });
    window.setTimeout(function () { stripAddToCartPrice(scope); }, 100);
  });

  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
}());
